import React, { useMemo } from 'react';
import { TableColumnsType } from 'antd';
import { useQuery } from 'react-query';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { EQueryKeys } from 'constants/query-keys';
import { claimMasterReserveApi } from 'services/apis';
import FormSection from 'components/ui/form-section';
import { MasterReserveResponseDto } from 'libs/pinnacle-sdk';
import { formatLocaleNumber } from 'utils/format';
import CustomTable from 'components/ui/custom-table';
import usePagination from 'hooks/usePagination';
import { roundMasterReserveValues } from '../../form';

interface MasterReserveHistoryProps {
  claimId: string;
  currency: string;
  isCurrencyMismatch: boolean;
}

const MasterReserveHistory: React.FC<MasterReserveHistoryProps> = ({
  claimId,
  currency,
  isCurrencyMismatch,
}) => {
  const { t } = useTranslation();
  const { currentPage, pageSize, onPageChange, onShowSizeChange } =
    usePagination();

  const {
    data: { items: masterReserveHistoryDataFetched, pagination: { total } } = {
      items: [],
      pagination: { total: 0 },
    },
    isLoading,
  } = useQuery(
    [
      EQueryKeys.LIST_MASTER_RESERVE_BY_CLAIM_ID,
      currency,
      claimId,
      currentPage,
      pageSize,
    ],
    () =>
      claimMasterReserveApi.getMasterReserveListByClaimId({
        claimId: claimId,
        currency: currency,
        page: currentPage,
        pageSize,
      }),
    {
      enabled: !!claimId,
      select: (data) => {
        if (isCurrencyMismatch) {
          data.items = data.items.map((item) => roundMasterReserveValues(item));
        }
        return data;
      },
    }
  );

  const dataSource = masterReserveHistoryDataFetched || [];

  const columns: TableColumnsType<MasterReserveResponseDto> = useMemo(
    () => [
      {
        title: t('claim.masterReserveHistory.dateChanged'),
        dataIndex: 'createdAt',
        key: 'createdAt',
        minWidth: 150,
        render: (value: Date) => dayjs(value).format('DD/MM/YYYY'),
      },
      {
        title: t('claim.masterReserveHistory.totalFguMarketLoss'),
        dataIndex: 'totalFguMarketLoss',
        key: 'totalFguMarketLoss',
        width: 180,
        render: (value: string) =>
          value && !isNaN(Number(value))
            ? formatLocaleNumber(Number(value))
            : '0',
      },
      {
        title: t('claim.masterReserveHistory.indemnityMaterialDamage'),
        dataIndex: 'indemnityMaterialDamage',
        key: 'indemnityMaterialDamage',
        width: 180,
        render: (value: string) =>
          value && !isNaN(Number(value))
            ? formatLocaleNumber(Number(value))
            : '0',
      },
      {
        title: t('claim.masterReserveHistory.indemnityBusinessInterruption'),
        dataIndex: 'indemnityBusinessInterruption',
        key: 'indemnityBusinessInterruption',
        width: 180,
        render: (value: string) =>
          value && !isNaN(Number(value))
            ? formatLocaleNumber(Number(value))
            : '0',
      },
      {
        title: t('claim.masterReserveHistory.totalIncurredCosts'),
        dataIndex: 'totalIncurredCosts',
        width: 180,
        key: 'totalIncurredCosts',
        render: (value: string) =>
          value && !isNaN(Number(value))
            ? formatLocaleNumber(Number(value))
            : '0',
      },
      {
        title: t('claim.masterReserveHistory.underliningSirGovtProgramsSir'),
        dataIndex: 'underliningSirGovtProgramsSir',
        key: 'underliningSirGovtProgramsSir',
        width: 180,
        render: (value: string) =>
          value && !isNaN(Number(value))
            ? formatLocaleNumber(Number(value))
            : '0',
      },
    ],
    [t]
  );

  return (
    <FormSection title={t('claim.masterReserveHistory.title')} collapsible>
      <CustomTable
        dataSource={dataSource}
        columns={columns}
        loading={isLoading}
        currentPage={currentPage}
        pageSize={pageSize}
        total={total}
        onPageChange={onPageChange}
        onShowSizeChange={onShowSizeChange}
        rowKey={(record: MasterReserveResponseDto) =>
          record.masterReserveId || ''
        }
      />
    </FormSection>
  );
};

export default MasterReserveHistory;
